/**
 * Audit log display helpers (Hub Audit Log UI).
 */

const IPV4_RE =
  /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d?\d)(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d?\d)){3})$/

function isIPv4(ip) {
  return Boolean(ip && IPV4_RE.test(String(ip).trim()))
}

function extractIPv4(ip) {
  if (!ip) return null
  const trimmed = String(ip).trim()
  if (isIPv4(trimmed)) return trimmed

  const lower = trimmed.toLowerCase()
  const mappedPrefix = '::ffff:'
  if (!lower.startsWith(mappedPrefix)) return null

  const suffix = trimmed.slice(mappedPrefix.length)
  if (isIPv4(suffix)) return suffix

  const parts = suffix.split(':')
  if (parts.length !== 2) return null

  const hi = parseInt(parts[0], 16)
  const lo = parseInt(parts[1], 16)
  if (Number.isNaN(hi) || Number.isNaN(lo)) return null

  return [
    (hi >> 8) & 0xff,
    hi & 0xff,
    (lo >> 8) & 0xff,
    lo & 0xff,
  ].join('.')
}

/** Prefer IPv4 for display; falls back to stored value. */
export function formatAuditIpAddress(ip) {
  if (!ip || ip === 'unknown') return ip || '—'
  const v4 = extractIPv4(ip)
  return v4 || ip
}

function parseDetailsObject(log) {
  if (!log.details) return null
  if (typeof log.details === 'object') return log.details
  if (typeof log.details === 'string') {
    try {
      return JSON.parse(log.details)
    } catch {
      return null
    }
  }
  return null
}

/** One-line summary for table preview. */
export function formatAuditDetailsPreview(log) {
  const summary = formatAuditDetailsSummary(log)
  if (summary) return summary
  const full = formatAuditDetailsRaw(log).replace(/\s+/g, ' ').trim()
  if (full === '—' || full.length <= 100) return full
  return `${full.slice(0, 100)}…`
}

/** Short human summary when available. */
export function formatAuditDetailsSummary(log) {
  if (log.message) return log.message

  const details = parseDetailsObject(log)
  if (!details) return null

  if (typeof details.summary === 'string') return details.summary
  if (typeof details.message === 'string') return details.message

  if (Array.isArray(details.changes) && details.changes.length > 0) {
    const labels = details.changes
      .slice(0, 3)
      .map((c) => c.label || c.key)
      .filter(Boolean)
    const more = details.changes.length - labels.length
    const base = labels.join('; ')
    return more > 0 ? `${base} (+${more} more)` : base
  }

  if (Array.isArray(details.keys) && details.keys.length > 0) {
    return `Updated ${details.keys.length} field${details.keys.length === 1 ? '' : 's'}`
  }

  return null
}

/** Full JSON or string for copy / technical view. */
export function formatAuditDetailsRaw(log) {
  if (!log.details) return log.message || '—'
  if (typeof log.details === 'string') {
    try {
      return JSON.stringify(JSON.parse(log.details), null, 2)
    } catch {
      return log.details
    }
  }
  try {
    return JSON.stringify(log.details, null, 2)
  } catch {
    return '—'
  }
}

export function getAuditDetailsChanges(log) {
  const details = parseDetailsObject(log)
  if (!details || !Array.isArray(details.changes)) return []
  return details.changes
}

const ENTITY_TYPE_LABELS = {
  crm_settings: 'CRM settings',
  user: 'User',
  room: 'Room',
  reservation: 'Reservation',
  lead: 'CRM case',
  invoice: 'Invoice',
  group: 'Group',
}

export function formatEntityTypeLabel(entityType) {
  if (!entityType) return '—'
  return ENTITY_TYPE_LABELS[entityType] || entityType.replace(/_/g, ' ')
}

/** Entity column/detail label including target record name when stored in details. */
export function formatAuditEntityLabel(log) {
  const typeLabel = formatEntityTypeLabel(log?.entity_type)
  const details = parseDetailsObject(log)

  if (details?.user?.name || details?.user?.email) {
    const who = details.user.name || details.user.email
    return `${typeLabel} — ${who}`
  }

  if (!log?.entity_type) return '—'
  return log.entity_id ? `${typeLabel} #${log.entity_id}` : typeLabel
}
